/* eslint-disable react/display-name */
import React, { ReactNode } from 'react';
import classNames from 'classnames';

import { Currency } from '~components/form-fields/inputs';
import Icon, { faCheck, faXmark, IconProp } from '~components/icon';

import { Column } from './types';

/**
 * Generic read-only renderer
 */
export function genericCellRender(mapper: (value: any, row?: any) => ReactNode, wrapInDiv = true, column?: Column) {
  return (value: any, row: any) =>
    wrapInDiv ? (
      <div className={classNames(['cell', 'readonly', column?.classNames?.cell])}>{mapper(value, row)}</div>
    ) : (
      mapper(value, row)
    );
}

/**
 * Checkbox renderer
 */
export function checkboxCellRender(column?: Column) {
  return (value: any, _row: any) => (
    <div className={classNames(['cell', column?.classNames?.cell])}>
      {value === true ? <Icon icon={faCheck} color="green" /> : <Icon icon={faXmark} color="red" />}
    </div>
  );
}

/**
 * Currency renderer
 */
export function currencyCellRender(
  id: string,
  noDecimals = false,
  zeroDisplay?: ((row: any, value: number) => React.ReactNode) | string,
  column?: Column,
) {
  return (value: number, row: any) => {
    const uniqueId = `${id}_${row?.id}`;
    const useZeroDisplay =
      value === null || isNaN(value) || (typeof zeroDisplay === 'function' && zeroDisplay(row, value));

    if (useZeroDisplay) {
      const placeholder = typeof zeroDisplay === 'function' ? zeroDisplay(row, value) : (zeroDisplay ?? '');

      return <div className={classNames(['cell', 'readonly', column?.classNames?.cell])}>{placeholder}</div>;
    }

    return (
      <Currency
        className={classNames(['cell', 'readonly', column?.classNames?.cell])}
        id={uniqueId}
        name={uniqueId}
        type="readonly"
        value={value}
        noDecimals={noDecimals}
        disabled
      />
    );
  };
}

/**
 * Clickable link renderer
 */
export function linkCellRender(onClick: (row: any) => void, hoverIcon?: IconProp, column?: Column) {
  return (value: any, row: any) =>
    value && (
      <div className={classNames(['on-hover', 'cell', column?.classNames?.cell])}>
        <span
          className="link"
          role="button"
          tabIndex={0}
          onClick={() => onClick(row)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              onClick(row);
            }
          }}
        >
          {value}
        </span>
        {hoverIcon && <Icon icon={hoverIcon} className="hover ms-2" size="1x" />}
      </div>
    );
}
